import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Edit, Check, X, Eye, Flag, Lightbulb, BarChart3, Library } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const COSTS = ["Free", "Paid", "Subscription", "Government Fee"];
const STATUSES = ["published", "archived", "pending"];
const BLANK = { name: "", category: "", url: "", description: "", cost: "Free", stage: "All", state: "All", industry: "All", eligibility: "Open to all businesses", contact: "", ai_summary: "", status: "published" };

function ResourceForm({ initial, categories, onSave, onClose }) {
  const [f, setF] = useState(initial || BLANK);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = () => { if (!f.name.trim() || !f.url.trim() || !f.category) { toast.error("Name, URL, and category required"); return; } onSave(f); };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="admin-resource-form" className="bg-[#0d0d0f] border-white/10 text-white max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading text-xl">{f.resource_id ? "Edit resource" : "Add resource"}</DialogTitle><DialogDescription className="sr-only">Resource fields</DialogDescription></DialogHeader>
        <input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="ar-name" placeholder="Name" className="bg-[#121214] border border-white/10 rounded-lg px-3 h-10 text-sm text-white" />
        <input value={f.url} onChange={(e) => set("url", e.target.value)} data-testid="ar-url" placeholder="URL (https://… or /internal-path)" className="bg-[#121214] border border-white/10 rounded-lg px-3 h-10 text-sm text-white" />
        <select value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="ar-category" className="bg-[#121214] border border-white/10 rounded-lg px-3 h-10 text-sm text-white">
          <option value="">Select category</option>{categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <textarea value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="ar-description" placeholder="Description" className="bg-[#121214] border border-white/10 rounded-lg p-3 text-sm text-white h-20 resize-none" />
        <div className="grid grid-cols-2 gap-2">
          <select value={f.cost} onChange={(e) => set("cost", e.target.value)} className="bg-[#121214] border border-white/10 rounded-lg px-3 h-10 text-sm text-white">{COSTS.map((c) => <option key={c}>{c}</option>)}</select>
          <input value={f.state} onChange={(e) => set("state", e.target.value)} placeholder="State (or All)" className="bg-[#121214] border border-white/10 rounded-lg px-3 h-10 text-sm text-white" />
        </div>
        <textarea value={f.ai_summary} onChange={(e) => set("ai_summary", e.target.value)} placeholder="AI recommendation summary (optional)" className="bg-[#121214] border border-white/10 rounded-lg p-3 text-sm text-white h-16 resize-none" />
        <Button onClick={save} data-testid="ar-save" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full">Save</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminResources() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [reports, setReports] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/resource-admin/overview").then(({ data }) => setData(data)).catch(() => {});
    api.get("/resource-admin/analytics").then(({ data }) => setAnalytics(data)).catch(() => {});
    api.get("/resource-admin/suggestions").then(({ data }) => setSuggestions(data.suggestions)).catch(() => {});
    api.get("/resource-admin/reports").then(({ data }) => setReports(data.reports)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveResource = async (f) => {
    try {
      if (f.resource_id) await api.patch(`/resource-admin/resources/${f.resource_id}`, f);
      else await api.post("/resource-admin/resources", f);
      toast.success("Saved"); setEditing(null); load();
    } catch { toast.error("Could not save resource"); }
  };
  const del = async (id) => { if (!window.confirm("Delete this resource?")) return; await api.delete(`/resource-admin/resources/${id}`); toast.success("Deleted"); load(); };
  const setStatus = async (id, status) => { await api.patch(`/resource-admin/resources/${id}`, { status }); load(); };
  const approve = async (id) => { await api.post(`/resource-admin/suggestions/${id}/approve`); toast.success("Approved & published"); load(); };
  const reject = async (id) => { await api.post(`/resource-admin/suggestions/${id}/reject`); toast.success("Rejected"); load(); };
  const resolveReport = async (id) => { await api.post(`/resource-admin/reports/${id}/resolve`); load(); };

  if (!data) return <p className="text-[#71717A]">Loading Resource CMS…</p>;
  const rows = data.resources.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()) || r.category.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="admin-resources">
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[[Library, "Resources", data.totals.total], [Eye, "Total views", analytics.total_views],
            [Lightbulb, "Pending suggestions", analytics.pending_suggestions], [Flag, "Open reports", analytics.open_reports]].map(([Icon, label, val], i) => (
            <div key={i} className="rounded-2xl border border-white/8 bg-[#121214] p-4">
              <Icon className="w-5 h-5 text-[#D4AF37] mb-2" />
              <div className="text-2xl font-semibold tabular-nums text-white">{val}</div>
              <div className="text-[10px] uppercase tracking-wider text-[#71717A]">{label}</div>
            </div>
          ))}
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-[#151310] p-4 mb-6" data-testid="admin-suggestion-queue">
          <div className="flex items-center gap-2 mb-3"><Lightbulb className="w-4 h-4 text-[#D4AF37]" /><h3 className="font-heading text-lg text-white">Suggestion approval queue</h3></div>
          <div className="space-y-2">
            {suggestions.map((s) => (
              <div key={s.suggestion_id} data-testid={`suggestion-${s.suggestion_id}`} className="flex items-center gap-3 rounded-lg border border-white/8 bg-[#121214] p-3">
                <div className="flex-1 min-w-0"><div className="text-sm text-white">{s.name} <span className="text-[11px] text-[#71717A]">· {s.category}</span></div><div className="text-xs text-[#52525B] truncate">{s.url}</div></div>
                <button onClick={() => approve(s.suggestion_id)} data-testid={`approve-${s.suggestion_id}`} className="text-emerald-400 hover:text-emerald-300"><Check className="w-4 h-4" /></button>
                <button onClick={() => reject(s.suggestion_id)} data-testid={`reject-${s.suggestion_id}`} className="text-red-400 hover:text-red-300"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {reports.length > 0 && (
        <div className="rounded-2xl border border-white/8 bg-[#121214] p-4 mb-6" data-testid="admin-reports">
          <div className="flex items-center gap-2 mb-3"><Flag className="w-4 h-4 text-red-400" /><h3 className="font-heading text-lg text-white">Outdated-resource reports</h3></div>
          <div className="space-y-2">
            {reports.map((r) => (
              <div key={r.report_id} className="flex items-center gap-3 text-sm">
                <span className="flex-1 text-[#D4D4D8]">{r.resource_name} — <span className="text-[#71717A]">{r.note}</span></span>
                <button onClick={() => resolveReport(r.report_id)} data-testid={`resolve-report-${r.report_id}`} className="text-[#D4AF37] text-xs hover:underline">Resolve</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mb-4">
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-resource-search" placeholder="Search resources…" className="flex-1 max-w-sm bg-[#121214] border border-white/10 rounded-full px-4 h-10 text-sm text-white focus:outline-none focus:border-[#D4AF37]/40" />
        <Button onClick={() => setEditing(BLANK)} data-testid="admin-add-resource" className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-full"><Plus className="w-4 h-4 mr-1" /> Add resource</Button>
      </div>

      <div className="space-y-2" data-testid="admin-resource-list">
        {rows.map((r) => (
          <div key={r.resource_id} data-testid={`admin-resource-${r.resource_id}`} className="flex items-center gap-3 rounded-xl border border-white/8 bg-[#121214] p-3">
            <div className="flex-1 min-w-0">
              <div className="text-sm text-white">{r.name} <span className="text-[11px] text-[#71717A]">· {r.category} · {r.views} views</span></div>
              <div className="text-xs text-[#52525B] truncate">{r.url}</div>
            </div>
            <select value={r.status} onChange={(e) => setStatus(r.resource_id, e.target.value)} data-testid={`admin-resource-status-${r.resource_id}`} className="text-[11px] bg-[#0d0d0f] border border-white/10 rounded-full px-2 py-1 text-[#A1A1AA]">
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            <button onClick={() => setEditing(r)} data-testid={`admin-edit-${r.resource_id}`} className="text-[#71717A] hover:text-white"><Edit className="w-3.5 h-3.5" /></button>
            <button onClick={() => del(r.resource_id)} data-testid={`admin-delete-${r.resource_id}`} className="text-[#71717A] hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>

      {editing && <ResourceForm initial={editing} categories={data.categories} onSave={saveResource} onClose={() => setEditing(null)} />}
    </div>
  );
}
